// apps/api/src/qxcircle/qxcircle-mention.ts
// Description: QXCircle mention normalize · match · mention feed builder.
// Owner: Masa Bayu
// Date: 2026-08-24

import { BadRequestException } from "./_nestjs-common";
import type {
  QxCircleFeedPost,
  QxCircleMentionFeedResponse,
  QxCircleNotification,
} from "./qxcircle.types";

const MENTION_PATTERN = /@([a-zA-Z0-9_]+)/g;

export function normalizeQxCircleMention(handleRaw: string): string {
  const trimmed = handleRaw.trim().replace(/^@+/, "");
  if (!trimmed || !/^[a-zA-Z0-9_]+$/.test(trimmed)) {
    throw new BadRequestException("Invalid mention handle");
  }
  return trimmed.toLowerCase();
}

export function extractQxCircleMentions(body: string): string[] {
  const found = new Set<string>();
  for (const match of body.matchAll(MENTION_PATTERN)) {
    const handle = match[1]?.toLowerCase();
    if (handle) found.add(handle);
  }
  return [...found];
}

export function postBodyHasMention(body: string, handle: string): boolean {
  return extractQxCircleMentions(body).includes(handle.toLowerCase());
}

export function buildQxCircleMentionFeed(
  handleRaw: string,
  posts: QxCircleFeedPost[],
  hiddenPostIds: Set<string>,
  blockedHandles: Set<string>,
  present: (post: QxCircleFeedPost) => QxCircleFeedPost,
): QxCircleMentionFeedResponse {
  const handle = normalizeQxCircleMention(handleRaw);
  const items = posts
    .filter(
      (post) =>
        !hiddenPostIds.has(post.id) &&
        !blockedHandles.has(post.authorHandle) &&
        postBodyHasMention(post.body, handle),
    )
    .map((post) => present(post))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  return {
    world: "qxcircle",
    slice: "mention",
    handle: `@${handle}`,
    count: items.length,
    items,
  };
}

export function createMentionNotification(
  seq: number,
  fromName: string,
  mentionedHandle: string,
  postId: string,
): QxCircleNotification {
  const slug = mentionedHandle.replace(/^@/, "");
  return {
    id: `qxcircle_ntf_mention_${seq}`,
    kind: "mention",
    title: `${fromName} mentioned @${slug}`,
    body: "Local mention simulated — not production push.",
    href: `/qxcircle/post/${encodeURIComponent(postId)}`,
    read: false,
    createdAt: new Date().toISOString(),
  };
}
